/**
 * Euchre bot.
 *
 * - Bidding: estimates the tricks the hand takes with a given trump suit (bowers, trump length and honours, off-suit
 *   aces, short suits to ruff with), adds an allowance for the partner (more if the partner is the dealer who will
 *   pick up the turned card, less if an opponent is), and orders up / names trump when that reaches three tricks.
 *   A very strong hand goes alone. The dealer must name trump in round 2 when everyone else passed.
 * - Dealer pick-up: discards the card whose loss hurts the hand least.
 * - Play: partnership-aware. Makers draw trumps with their bowers and best trumps, defenders lead off-suit aces and
 *   avoid leading trump; everyone takes tricks as cheaply as possible and lets a winning partner keep the trick.
 *   NORMAL and above track the cards played to recognise masters.
 * The bot only ever reads its own seat's view.
 */
import { parseCard, SUITS, type CardId, type Suit } from '../../core/cards.js';
import { chance, type BotContext, type BotEngine } from '../../bots/types.js';
import {
  euchreBestSuit,
  euchreDeck,
  euchreDiscardChoice,
  euchrePartnerOf,
  euchreSuit,
  euchreTrickWinner,
  euchreTrumpPower,
  type EuchreAction,
  type EuchreLegal,
  type EuchreTrickPlay,
  type EuchreView,
} from './engine.js';

/** Tricks a partner is assumed to add on average when deciding whether the team can take three. */
const PARTNER_ALLOWANCE = 0.8;
const ORDER_THRESHOLD = 3.0;
const CALL_THRESHOLD = 2.9;
const ALONE_THRESHOLD = 4.4;

const TRUMP_VALUE: Record<number, number> = { 30: 0.95, 29: 0.85, 14: 0.8, 13: 0.65, 12: 0.5, 10: 0.35, 9: 0.3 };

/** Tricks the hand is expected to take alone with `trump` as trump. */
export function estimateEuchreTricks(hand: readonly CardId[], trump: Suit): number {
  let est = 0;
  let trumps = 0;
  const side = new Map<Suit, number>(SUITS.map((s) => [s, 0]));
  for (const c of hand) {
    const p = euchreTrumpPower(c, trump);
    if (p !== null) {
      est += TRUMP_VALUE[p] ?? 0.3;
      trumps++;
    } else {
      const suit = parseCard(c).suit;
      side.set(suit, (side.get(suit) ?? 0) + 1);
      if (parseCard(c).rank === 14) est += 0.6;
      else if (parseCard(c).rank === 13 && hand.some((o) => parseCard(o).suit === suit && parseCard(o).rank === 14)) est += 0.2;
    }
  }
  if (trumps >= 3) est += (trumps - 2) * 0.25;
  if (trumps >= 2) {
    for (const [, n] of side) if (n === 0) est += 0.35;
  }
  return est;
}

/** Strength of a dealer's hand once the turned card is picked up and the weakest card thrown away. */
function strengthAfterPickup(hand: readonly CardId[], turnCard: CardId, trump: Suit): number {
  const six = [...hand, turnCard];
  const drop = discardFor(six, trump);
  return estimateEuchreTricks(six.filter((c) => c !== drop), trump);
}

/** The discard that leaves the strongest hand. */
function discardFor(hand: readonly CardId[], trump: Suit): CardId {
  let best = euchreDiscardChoice(hand, trump);
  let bestEst = -1;
  for (const c of hand) {
    const est = estimateEuchreTricks(hand.filter((x) => x !== c), trump);
    if (est > bestEst + 1e-9) {
      bestEst = est;
      best = c;
    }
  }
  return best;
}

function unseenCards(view: EuchreView, ctx: BotContext): Set<CardId> {
  const seen = new Set<CardId>(view.hand);
  view.trick.forEach((p) => seen.add(p.card));
  const n = view.tricks.length;
  view.tricks.forEach((t, i) => {
    // Forgetful bots only recall the most recent tricks.
    if (ctx.profile.memory >= 1 || i >= n - Math.max(1, Math.round(n * ctx.profile.memory))) for (const p of t.plays) seen.add(p.card);
  });
  return new Set(euchreDeck().filter((c) => !seen.has(c)));
}

function chooseCard(view: EuchreView, legal: CardId[], ctx: BotContext): CardId {
  if (legal.length === 1) return legal[0]!;
  const me = view.seat!;
  const partner = euchrePartnerOf(me);
  const partnerIn = view.sittingOut !== partner;
  const trump = view.trump!;
  const trick: readonly EuchreTrickPlay[] = view.trick;
  const unseen = ctx.profile.memory > 0 ? unseenCards(view, ctx) : null;
  const power = (id: CardId) => {
    const p = euchreTrumpPower(id, trump);
    return p !== null ? 100 + p : parseCard(id).rank;
  };
  const isTrump = (id: CardId) => euchreTrumpPower(id, trump) !== null;
  const lowest = (cards: CardId[]) => [...cards].sort((a, b) => power(a) - power(b))[0]!;
  const highest = (cards: CardId[]) => [...cards].sort((a, b) => power(b) - power(a))[0]!;
  const isMaster = (id: CardId) => {
    if (!unseen) return power(id) >= 114 || (!isTrump(id) && parseCard(id).rank === 14);
    for (const u of unseen) {
      if (isTrump(id) ? power(u) > power(id) : euchreSuit(u, trump) === euchreSuit(id, trump) && power(u) > power(id) && !isTrump(u)) return false;
    }
    return true;
  };
  const makersTeam = view.maker !== null && view.maker % 2 === me % 2;
  const wouldWin = (card: CardId) => euchreTrickWinner([...trick, { seat: me, card }], trump) === me;
  const dump = (cards: CardId[]) => {
    const plain = cards.filter((c) => !isTrump(c));
    return lowest(plain.length ? plain : cards);
  };

  if (trick.length === 0) {
    const trumps = legal.filter(isTrump);
    const plain = legal.filter((c) => !isTrump(c));
    if (makersTeam && trumps.length > 0) {
      // Draw the opponents' trumps out: lead the best trump when it is likely to win or we hold several.
      const top = highest(trumps);
      if (trumps.length >= 2 || isMaster(top)) return top;
    }
    const masters = plain.filter((c) => isMaster(c));
    if (masters.length) return highest(masters);
    if (!makersTeam && plain.length) return lowest(plain);
    if (plain.length) return lowest(plain);
    return lowest(legal);
  }

  const winnerSeat = euchreTrickWinner(trick, trump);
  const winnerCard = trick.find((p) => p.seat === winnerSeat)!.card;
  const last = trick.length === (view.sittingOut === null ? 3 : 2);
  if (partnerIn && winnerSeat === partner) {
    // Partner is winning: keep our winners unless a later opponent could still take it with a card we can beat.
    if (last || isMaster(winnerCard)) return dump(legal);
    const over = legal.filter((c) => wouldWin(c) && !isTrump(c));
    if (over.length && power(winnerCard) < 12) return lowest(over);
    return dump(legal);
  }
  const winners = legal.filter(wouldWin);
  if (winners.length === 0) return dump(legal);
  if (last) return lowest(winners);
  const safe = winners.filter((c) => isMaster(c) || isTrump(c));
  return lowest(safe.length ? safe : winners);
}

export const euchreBot: BotEngine<EuchreView, EuchreLegal, EuchreAction> = {
  decideAction(view, legal, ctx) {
    if (legal.kind === 'bid') return this.chooseBet!(view, legal, ctx);
    if (legal.kind === 'discard') return { type: 'discard', card: discardFor(view.hand, view.trump!) };
    if (legal.kind === 'play') return this.chooseCard!(view, legal, ctx);
    throw new Error('Bot asked to act without legal actions');
  },

  chooseBet(view, legal, ctx) {
    if (legal.kind !== 'bid') return this.handleTimeout(view, legal, ctx);
    const me = view.seat!;
    const noise = ctx.difficulty === 'BEGINNER' ? (ctx.rng.next() - 0.5) * 1.6 : ctx.difficulty === 'EASY' ? (ctx.rng.next() - 0.5) * 0.9 : 0;
    const alone = (est: number) => legal.canAlone && (ctx.difficulty === 'NORMAL' || ctx.difficulty === 'HARD' || ctx.difficulty === 'EXPERT') && est >= ALONE_THRESHOLD;

    if (legal.round === 1) {
      const suit = legal.suits[0]!;
      const dealer = view.dealer;
      let est: number;
      if (me === dealer) est = strengthAfterPickup(view.hand, view.turnCard!, suit) + PARTNER_ALLOWANCE;
      else {
        est = estimateEuchreTricks(view.hand, suit) + PARTNER_ALLOWANCE;
        // The dealer gets the turned card: good for their partner, bad for us if they are an opponent.
        est += dealer === euchrePartnerOf(me) ? 0.35 : -0.35;
      }
      est += noise;
      if (est >= ORDER_THRESHOLD) return { type: 'orderUp', alone: alone(est - PARTNER_ALLOWANCE) };
      return { type: 'pass' };
    }

    let bestSuit: Suit = legal.suits[0]!;
    let bestEst = -1;
    for (const s of legal.suits) {
      const e = estimateEuchreTricks(view.hand, s);
      if (e > bestEst) {
        bestEst = e;
        bestSuit = s;
      }
    }
    const est = bestEst + PARTNER_ALLOWANCE + noise;
    if (est >= CALL_THRESHOLD || !legal.canPass) return { type: 'call', suit: bestSuit, alone: alone(bestEst) };
    return { type: 'pass' };
  },

  chooseCard(view, legal, ctx) {
    if (legal.kind !== 'play') return this.handleTimeout(view, legal, ctx);
    const cards = legal.cards;
    if (cards.length === 1) return { type: 'play', card: cards[0]! };
    if (chance(ctx, ctx.profile.mistakeRate)) return { type: 'play', card: cards[ctx.rng.int(cards.length)]! };
    const card = chooseCard(view, cards, ctx);
    return { type: 'play', card: cards.includes(card) ? card : cards[0]! };
  },

  evaluateState(view) {
    const me = view.seat;
    if (me === null) return 0;
    const team = me % 2;
    const diff = view.teamScores[team]! - view.teamScores[1 - team]!;
    return Math.max(0, Math.min(1, 0.5 + diff / 20));
  },

  calculateRisk(view, legal) {
    if (legal.kind === 'bid') {
      const suit = legal.suits[0]!;
      return Math.min(1, Math.max(0, (ORDER_THRESHOLD - estimateEuchreTricks(view.hand, suit)) / 5));
    }
    return 0;
  },

  handleTimeout(view, legal) {
    if (legal.kind === 'bid') {
      if (legal.canPass) return { type: 'pass' };
      return { type: 'call', suit: euchreBestSuit(view.hand, view.turnSuit), alone: false };
    }
    if (legal.kind === 'discard') return { type: 'discard', card: euchreDiscardChoice(view.hand, view.trump!) };
    if (legal.kind === 'play') {
      const trump = view.trump!;
      const power = (id: CardId) => {
        const p = euchreTrumpPower(id, trump);
        return p !== null ? 100 + p : parseCard(id).rank;
      };
      return { type: 'play', card: [...legal.cards].sort((a, b) => power(a) - power(b))[0]! };
    }
    throw new Error('No legal action');
  },
};
